import { useState } from 'react'
import { Plus, Building2, MapPin, Users, Settings, Trash2, Edit } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { 
  useGetPropertiesQuery, 
  useCreatePropertyMutation, 
  useDeletePropertyMutation 
} from '@/store/api'
import { Property } from '@/types'

import CreatePropertyModal from './components/CreatePropertyModal'

export default function PropertiesPage() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const { data: propertiesResponse, isLoading, error } = useGetPropertiesQuery()
  const [createProperty] = useCreatePropertyMutation()
  const [deleteProperty] = useDeletePropertyMutation()

  const properties = propertiesResponse?.data || []

  const filteredProperties = properties.filter(property =>
    property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.address.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCreateProperty = async (propertyData: Partial<Property>) => {
    try {
      await createProperty(propertyData).unwrap()
      setShowCreateModal(false)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug('Failed to create property:', error)
      }
      // Error handling can be added here (e.g., toast notification)
    }
  }

  const handleDeleteProperty = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
      try {
        await deleteProperty(id).unwrap()
      } catch (error) {
        if (import.meta.env.DEV) {
          console.debug('Failed to delete property:', error)
        }
        // Error handling can be added here (e.g., toast notification)
      }
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'inactive':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'commercial':
        return <Building2 className="w-5 h-5" />
      case 'residential':
        return <Users className="w-5 h-5" />
      case 'industrial':
        return <Settings className="w-5 h-5" />
      default:
        return <Building2 className="w-5 h-5" />
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-red-600">Failed to load properties. Please try again.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Properties</h1>
          <p className="text-gray-600 mt-1">Manage your property portfolio</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Property
        </Button>
      </div>

      <div className="mb-6">
        <Input
          placeholder="Search properties..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProperties.map((property) => (
          <Card key={property.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    {getTypeIcon(property.type)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{property.name}</CardTitle>
                    <Badge className={getStatusColor(property.status || 'active')}>
                      {property.status || 'active'}
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDeleteProperty(property.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">{property.address}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium capitalize">{property.type}</span>
                </div>
                
                {property.units && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Units:</span>
                    <span className="font-medium">{property.units}</span>
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-4 pt-3 border-t">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">
                      {property.equipmentCount || 0}
                    </div>
                    <div className="text-xs text-gray-600">Equipment</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-600">
                      {property.alerts || 0}
                    </div>
                    <div className="text-xs text-gray-600">Alerts</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">
                      {property.workOrders || 0}
                    </div>
                    <div className="text-xs text-gray-600">Work Orders</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredProperties.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No properties found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by adding your first property.'}
          </p>
          {!searchTerm && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Property
            </Button>
          )}
        </div>
      )}

      <CreatePropertyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateProperty}
      />
    </div>
  )
}